/**
 * Manage Booking — renders booking details with reschedule/cancel actions.
 * Reads token from URL param (?token=XXX) and fetches from proxy API.
 */

if (!customElements.get('manage-booking')) {
  customElements.define('manage-booking', class ManageBooking extends HTMLElement {
    connectedCallback() {
      this.proxyBase = '/a/proxy/bookings';
      this.token = new URLSearchParams(window.location.search).get('token');

      if (!this.token) {
        this.innerHTML = '<div style="text-align:center;padding:40px;color:rgba(54,72,97,0.5);">No booking token provided.</div>';
        return;
      }

      this.rescheduleOpen = false;
      this.rescheduleMonth = null;
      this.rescheduleSlots = {};
      this.loadBooking();
    }

    async loadBooking() {
      this.innerHTML = '<div style="text-align:center;padding:40px;color:rgba(54,72,97,0.4);font-family:Inter,sans-serif;">Loading booking...</div>';

      try {
        const res = await fetch(`${this.proxyBase}/manage/${this.token}.json`);
        if (!res.ok) throw new Error('Booking not found');
        this.data = await res.json();
        this.render();
      } catch (err) {
        this.innerHTML = '<div style="text-align:center;padding:40px;color:rgba(54,72,97,0.5);font-family:Inter,sans-serif;">Booking not found or link has expired.</div>';
      }
    }

    render() {
      const { booking, event, slot, location, can_reschedule, can_cancel } = this.data;
      const S = (cls) => `manage-booking__${cls}`;

      let html = `<div class="${S('wrap')}">`;

      // Back link
      html += `<a href="/pages/my-bookings" class="${S('back')}">&larr; My Bookings</a>`;

      // Status
      const statusMap = { confirmed: { color: '#9FD7B0', label: 'Confirmed' }, cancelled: { color: '#FFA9BB', label: 'Cancelled' }, reserved: { color: '#FFCC6C', label: 'Reserved' } };
      const st = statusMap[booking.status] || { color: '#F3E9E2', label: booking.status };

      // Card
      html += `<div class="${S('card')}">
        <div class="${S('header')}">
          <div>
            <div class="${S('title')}">${event.name}</div>
            <div class="${S('sub')}">${booking.guest_count} guest${booking.guest_count === 1 ? '' : 's'}</div>
          </div>
          <span class="${S('badge')}" style="background:${st.color};color:#fff;">${st.label}</span>
        </div>
        <div class="${S('rows')}">
          <div class="${S('row')}">
            <span class="${S('icon')}">&#128197;</span>
            <div><div class="${S('label')}">${slot.date}</div><div class="${S('val')}">${slot.start_time} - ${slot.end_time}</div></div>
          </div>
          <div class="${S('row')}">
            <span class="${S('icon')}">&#128205;</span>
            <div><div class="${S('label')}">${location.name}</div><div class="${S('val')}">${location.address}</div></div>
          </div>`;

      if (booking.children_ages && booking.children_ages.length > 0) {
        html += `<div class="${S('row')}">
            <span class="${S('icon')}">&#128118;</span>
            <div><div class="${S('label')}">Children</div><div class="${S('val')}">Age: ${booking.children_ages.join(', ')}</div></div>
          </div>`;
      }

      html += '</div></div>';

      // Actions
      if (can_reschedule || can_cancel) {
        html += `<div class="${S('actions')}">`;

        if (can_reschedule) {
          if (this.rescheduleOpen) {
            html += this.renderReschedule();
          } else {
            html += `<button class="${S('btn')} ${S('btn-outline')}" data-action="open-reschedule">Reschedule</button>`;
          }
        }

        if (can_cancel) {
          html += `<button class="${S('btn')} ${S('btn-red')}" data-action="cancel-booking">Cancel Booking</button>`;
        }

        html += '</div>';
      }

      html += '</div>';
      this.innerHTML = html;
      this.bindEvents();
    }

    renderReschedule() {
      const S = (cls) => `manage-booking__${cls}`;

      if (!this.rescheduleMonth) {
        const today = new Date();
        this.rescheduleMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
        this.loadRescheduleSlots(this.rescheduleMonth);
      }

      const [y, m] = this.rescheduleMonth.split('-').map(Number);
      const monthLabel = new Date(y, m - 1, 1).toLocaleDateString('en', { month: 'long', year: 'numeric' });

      let html = `<div class="${S('card')}">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
          <span class="${S('title')}" style="font-size:16px;">Pick a new date & time</span>
          <button class="${S('btn-link')}" data-action="close-reschedule">Cancel</button>
        </div>
        <div class="${S('nav')}">
          <button class="${S('nav-btn')}" data-action="prev-month">&lsaquo;</button>
          <span class="${S('nav-label')}">${monthLabel}</span>
          <button class="${S('nav-btn')}" data-action="next-month">&rsaquo;</button>
        </div>
        <div id="manage-reschedule-slots">`;

      const slots = this.rescheduleSlots[this.rescheduleMonth];
      if (!slots) {
        html += `<div style="text-align:center;padding:16px;color:rgba(54,72,97,0.4);font-size:14px;">Loading...</div>`;
      } else if (slots.length === 0) {
        html += `<div style="text-align:center;padding:16px;color:rgba(54,72,97,0.4);font-size:14px;">No available slots this month.</div>`;
      } else {
        const grouped = {};
        slots.forEach(s => { (grouped[s.date] = grouped[s.date] || []).push(s); });
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);

        Object.entries(grouped).forEach(([date, daySlots]) => {
          const d = new Date(date + 'T00:00:00');
          let dayLabel = d.toLocaleDateString('en', { weekday: 'long' });
          if (d.getTime() === today.getTime()) dayLabel = 'Today';
          if (d.getTime() === tomorrow.getTime()) dayLabel = 'Tomorrow';
          const dateLabel = d.toLocaleDateString('en', { month: 'short', day: 'numeric' });

          html += `<div class="${S('day')}">
            <div class="${S('day-hdr')}"><span class="${S('day-name')}">${dayLabel}</span> <span class="${S('day-date')}">${dateLabel}</span></div>
            <div class="${S('slots')}">`;

          daySlots.forEach(slot => {
            html += `<button class="${S('slot')}" data-action="reschedule" data-slot-id="${slot.id}" data-slot-label="${dayLabel} at ${slot.start_time} - ${slot.end_time}">${slot.start_time} - ${slot.end_time}</button>`;
          });

          html += '</div></div>';
        });
      }

      html += '</div></div>';
      return html;
    }

    async loadRescheduleSlots(month) {
      try {
        const res = await fetch(`${this.proxyBase}/manage/${this.token}/reschedule_options?month=${month}`);
        if (!res.ok) return;
        const data = await res.json();
        this.rescheduleSlots[month] = data.slots;
        this.render();
      } catch (err) {
        console.error('Failed to load reschedule slots:', err);
      }
    }

    async doReschedule(slotId) {
      try {
        const res = await fetch(`${this.proxyBase}/manage/${this.token}/reschedule.json`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ new_slot_id: slotId })
        });

        const data = await res.json();
        if (data.success) {
          this.rescheduleOpen = false;
          this.rescheduleSlots = {};
          this.rescheduleMonth = null;
          this.loadBooking();
          if (window.MontisNotification) window.MontisNotification.success('Booking rescheduled!');
        } else {
          if (window.MontisNotification) window.MontisNotification.error(data.error || 'Failed to reschedule');
        }
      } catch (err) {
        if (window.MontisNotification) window.MontisNotification.error('Failed to reschedule');
      }
    }

    async doCancel() {
      try {
        const res = await fetch(`${this.proxyBase}/manage/${this.token}/cancel.json`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });

        const data = await res.json();
        if (data.success) {
          this.loadBooking();
          if (window.MontisNotification) window.MontisNotification.success('Booking cancelled.');
        } else {
          if (window.MontisNotification) window.MontisNotification.error(data.error || 'Failed to cancel');
        }
      } catch (err) {
        if (window.MontisNotification) window.MontisNotification.error('Failed to cancel');
      }
    }

    bindEvents() {
      this.querySelectorAll('[data-action]').forEach(el => {
        el.addEventListener('click', (e) => {
          const action = e.currentTarget.dataset.action;

          switch (action) {
            case 'open-reschedule':
              this.rescheduleOpen = true;
              this.render();
              break;

            case 'close-reschedule':
              this.rescheduleOpen = false;
              this.render();
              break;

            case 'prev-month': {
              const [y, m] = this.rescheduleMonth.split('-').map(Number);
              const d = new Date(y, m - 2, 1);
              const now = new Date();
              if (d.getFullYear() < now.getFullYear() || (d.getFullYear() === now.getFullYear() && d.getMonth() < now.getMonth())) return;
              this.rescheduleMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
              if (!this.rescheduleSlots[this.rescheduleMonth]) this.loadRescheduleSlots(this.rescheduleMonth);
              else this.render();
              break;
            }

            case 'next-month': {
              const [y, m] = this.rescheduleMonth.split('-').map(Number);
              const d = new Date(y, m, 1);
              this.rescheduleMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
              if (!this.rescheduleSlots[this.rescheduleMonth]) this.loadRescheduleSlots(this.rescheduleMonth);
              else this.render();
              break;
            }

            case 'reschedule':
              if (confirm(`Move to ${e.currentTarget.dataset.slotLabel}?`)) {
                this.doReschedule(e.currentTarget.dataset.slotId);
              }
              break;

            case 'cancel-booking':
              if (confirm('Are you sure you want to cancel this booking? This cannot be undone.')) {
                this.doCancel();
              }
              break;
          }
        });
      });
    }
  });
}
